import React, { useState, useEffect } from 'react';
import './DraftTutorial.css';

/* ─── Shared K logo ─────────────────────────────────────────────────────────── */

const K_LOGO_ICON = (
  <img src="/kickopp-k-logo.svg" alt="Kickopp"
       style={{ width: '56px', height: '56px', objectFit: 'contain' }} />
);

/* ─── Illustrations ──────────────────────────────────────────────────────────── */

function CaptainIllustration() {
  // Same card style as DraftWindowIllustration — no photos, real stats
  const cards = [
    { atk: 9,  def: 10, name: 'K. Halter', pos: 'WF',  col: '#3b82f6', captain: false },
    { atk: 9,  def: 13, name: 'A. Boban',  pos: 'CM',  col: '#10b981', captain: true  },
    { atk: 6,  def: 15, name: 'T. Moran',  pos: 'DCB', col: '#ef4444', captain: false },
  ];
  return (
    <div className="dt-visual dt-visual--draft">
      {/* paddingTop gives room for the absolute CAPTAIN banner above the centre card */}
      <div className="dt-visual__draft-cards" style={{ paddingTop: '34px' }}>
        {cards.map((c, i) => (
          <div
            key={i}
            className="dt-visual__draft-card"
            style={{
              '--cc': c.col,
              border: `1.5px solid ${c.captain ? '#eab308' : c.col + '66'}`,
              boxShadow: c.captain ? '0 0 22px -4px #eab308, 0 8px 24px rgba(0,0,0,0.4)' : 'none',
              transform: c.captain ? 'translateY(-8px) scale(1.05)' : 'none',
              opacity: c.captain ? 1 : 0.55,
            }}
          >
            {/* CAPTAIN banner above the card */}
            {c.captain && (
              <div style={{
                position: 'absolute', top: '-26px', left: '50%', transform: 'translateX(-50%)',
                background: '#eab308', color: '#000',
                fontSize: '10px', fontWeight: '800',
                padding: '3px 10px', borderRadius: '8px',
                whiteSpace: 'nowrap', letterSpacing: '0.3px',
              }}>⭐ CAPTAIN</div>
            )}

            {/* ATK / DEF stats row */}
            <div className="dt-visual__draft-stats">
              <span className="dt-visual__draft-atk">{c.atk}</span>
              <span className="dt-visual__draft-def">{c.def}</span>
            </div>

            {/* Face placeholder — shorter than DraftWindowIllustration to keep modal compact */}
            <div style={{
              height: '44px', borderRadius: '7px',
              background: `linear-gradient(135deg, ${c.captain ? '#eab30818' : c.col + '18'}, ${c.col}08)`,
              border: `1px solid ${c.captain ? '#eab30830' : c.col + '30'}`,
            }} />

            {/* Position badge row — pos on left, C badge on right for captain */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
              <div className="dt-visual__draft-pos" style={{ background: c.col }}>{c.pos}</div>
              {c.captain && (
                <div style={{
                  background: '#eab308', color: '#000',
                  fontWeight: 900, fontSize: '11px',
                  padding: '2px 7px', borderRadius: '5px',
                }}>C</div>
              )}
            </div>

            <div className="dt-visual__draft-name">{c.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoostsIllustration() {
  // Stat row with – value + buttons, matching the real SELECTED CARD panel
  const StatRow = ({ label, value, col }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <span style={{ color: col, fontWeight: 900, fontSize: '13px', minWidth: '32px' }}>{label}:</span>
      <span style={{ color: '#fff', fontWeight: 900, fontSize: '15px', minWidth: '20px', textAlign: 'center' }}>{value}</span>
      {/* – button */}
      <div style={{
        width: '26px', height: '26px', borderRadius: '6px',
        border: '2px solid #ef444488', background: 'rgba(239,68,68,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#ef4444', fontWeight: 900, fontSize: '16px', lineHeight: 1,
      }}>−</div>
      {/* + button */}
      <div style={{
        width: '26px', height: '26px', borderRadius: '6px',
        border: '2px solid #22c55e88', background: 'rgba(34,197,94,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#22c55e', fontWeight: 900, fontSize: '16px', lineHeight: 1,
      }}>+</div>
    </div>
  );

  return (
    <div className="dt-visual" style={{ justifyContent: 'center', gap: '18px', alignItems: 'center' }}>
      {/* YOUR BOOSTS panel */}
      <div style={{
        background: 'rgba(4,8,20,0.6)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '14px',
        padding: '14px 18px',
        minWidth: '170px',
      }}>
        <div style={{ color: '#fff', fontWeight: 900, fontSize: '13px', marginBottom: '10px', letterSpacing: '0.5px' }}>
          YOUR BOOSTS <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '11px' }}>(4 avail.)</span>
        </div>
        {[
          { label: 'ATK', val: '3 / 3', col: '#60a5fa', used: true },
          { label: 'DEF', val: '1 / 4', col: '#f87171', used: false },
        ].map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '4px', height: '26px', borderRadius: '2px',
              background: b.used ? b.col : 'rgba(255,255,255,0.15)',
            }} />
            <span style={{ color: b.col, fontWeight: 900, fontSize: '13px', minWidth: '34px' }}>{b.label}</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px', marginLeft: 'auto' }}>{b.val}</span>
          </div>
        ))}
      </div>

      {/* Arrow */}
      <div style={{ color: '#64748b', fontSize: '24px', fontWeight: 900 }}>→</div>

      {/* SELECTED CARD panel — mirrors the real in-game panel */}
      <div style={{
        background: 'rgba(4,8,20,0.7)',
        border: '1.5px solid rgba(96,165,250,0.35)',
        borderRadius: '14px',
        padding: '12px 14px',
        minWidth: '160px',
        boxShadow: '0 0 18px rgba(96,165,250,0.12)',
      }}>
        {/* Header */}
        <div style={{
          color: '#fff', fontWeight: 900, fontSize: '12px', letterSpacing: '1px',
          textAlign: 'center', marginBottom: '12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '8px',
        }}>SELECTED CARD</div>

        <StatRow label="ATK" value={8}  col="#60a5fa" />
        <StatRow label="DEF" value={13} col="#f87171" />

        {/* Player name */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: '10px', marginBottom: '8px',
        }}>
          <span style={{ color: '#cbd5e1', fontWeight: 700, fontSize: '13px' }}>D. Okonkwo</span>
        </div>

        {/* Position badge */}
        <div style={{
          background: '#22c55e', color: '#fff',
          fontWeight: 900, fontSize: '12px', letterSpacing: '0.5px',
          textAlign: 'center', padding: '5px 0', borderRadius: '7px',
        }}>DM</div>
      </div>
    </div>
  );
}

/* ─── Step definitions ───────────────────────────────────────────────────────── */

const STEPS = [
  {
    id: 'welcome',
    icon: K_LOGO_ICON,
    accent: '#B6499A',
    badge: 'WARM UP GUIDE',
    title: 'Warm Up — Prepare Your Team',
    body: <>
      Before the battle kicks off, each manager gets a chance to set their Captain
      and distribute Boost points earned from their Tactics.
      <br />
      These boosts can be the difference between a win and a loss!
    </>,
    Visual: null,
    hint: null,
  },
  {
    id: 'boosts',
    icon: K_LOGO_ICON,
    accent: '#60a5fa',
    badge: '1/2 · BOOSTS',
    title: 'Add Your Boosts',
    body: <>
      Check the <strong style={{ color: '#60a5fa' }}>YOUR BOOSTS</strong> panel — it shows how many
      ATK and DEF boost points you earned by matching Tactic requirements during the Draft.
      <br />
      Tap any of your players to assign those boosts and strengthen them before the match.
    </>,
    Visual: BoostsIllustration,
    hint: '📍 Check the YOUR BOOSTS panel for available points to distribute',
  },
  {
    id: 'captain',
    icon: K_LOGO_ICON,
    accent: '#eab308',
    badge: '2/2 · CAPTAIN',
    title: 'Choose Your Captain',
    body: <>
      Choose a player to designate as <strong style={{ color: '#eab308' }}>Captain</strong>.
      When attacked by the opponent, the Captain is not eliminated — the{' '}
      <strong style={{ color: '#eab308' }}>Captain (C)</strong> badge is removed but the card{' '}
      <strong style={{ color: '#eab308' }}>stays in the match</strong> for one more attack.
      Because of this extra life, the Captain counts as{' '}
      <strong style={{ color: '#eab308' }}>2 cards</strong> on the final score.
    </>,
    Visual: CaptainIllustration,
    hint: '📍 Choose a player to designate as Captain',
  },
];

/* ─── Main component ─────────────────────────────────────────────────────────── */

export default function WarmupTutorial({ onComplete, onSkip, isVertical = false }) {
  const [step, setStep]       = useState(0);
  const [animDir, setAnimDir] = useState('in');

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;

  const navigate = (delta) => {
    setAnimDir(delta > 0 ? 'out-left' : 'out-right');
    setTimeout(() => {
      setStep(s => s + delta);
      setAnimDir('in');
    }, 200);
  };

  const handleNext = () => isLast ? onComplete() : navigate(1);
  const handleBack = () => navigate(-1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft' && !isFirst) handleBack();
      if (e.key === 'Escape') onSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const Visual = current.Visual;

  return (
    <div className="dt">
      <div className="dt__overlay" />
      <div
        className={`dt__card dt__card--${animDir}`}
        style={{ '--accent': current.accent }}
        onClick={e => e.stopPropagation()}
      >
        <button className="dt__close" onClick={onSkip} title="Close (Esc)">✕</button>
        <div className="dt__accent-bar" />

        <div className="dt__header">
          <div className="dt__icon-wrap">
            <span className="dt__icon">{current.icon}</span>
          </div>
          <div className="dt__header-text">
            <div className="dt__badge">{current.badge}</div>
            <h2 className="dt__title">{current.title}</h2>
          </div>
        </div>

        <p className="dt__body">{current.body}</p>

        {current.hint && (
          <div className="dt__location-hint">
            {typeof current.hint === 'function' ? current.hint(isVertical) : current.hint}
          </div>
        )}

        {Visual && (
          <div className="dt__visual-wrap">
            <Visual />
          </div>
        )}

        <div className="dt__footer">
          <div className="dt__dots">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                className={`dt__dot ${i === step ? 'dt__dot--active' : ''} ${i < step ? 'dt__dot--done' : ''}`}
                style={i === step ? { background: current.accent, boxShadow: `0 0 8px ${current.accent}88` } : {}}
                onClick={() => { if (i !== step) { setAnimDir(i > step ? 'out-left' : 'out-right'); setTimeout(() => { setStep(i); setAnimDir('in'); }, 200); } }}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
          <div className="dt__nav">
            {!isFirst && (
              <button className="dt__btn dt__btn--back" onClick={handleBack}>← Back</button>
            )}
            <button
              className="dt__btn dt__btn--next"
              style={{ background: current.accent, boxShadow: `0 6px 20px ${current.accent}55` }}
              onClick={handleNext}
            >
              {isFirst ? "Let's Go →" : isLast ? "⚽ Let's Play!" : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
